// Bereich "Buchhaltung (intern)": die Ansichten der Parallelbuchhaltung als eigenstaendige, klar getrennte Seiten. Auf JEDER Seite steht der Hinweis
// "intern / vorlaeufig / nicht massgeblich". Funktionen und API-Aufrufe sind unveraendert (die Komponenten stammen aus den bisherigen Parallelbuchhaltungs-Reitern).
import { useState, useEffect, useCallback } from "react";
import THEME from "../../../../theme";
import { pbApi } from "../../../belege/belegeApi";
import { Meldung } from "../../../belege/ui";
import { Seitenkopf, PbBanner, Abschnitt, Kachel, kachelRaster } from "../../ui/Bausteine";
import { Einstellungen, Zuordnung, Lauf, Buchungen, Uebersicht } from "../belege/BelegeParallelbuch";
import { Auswertung } from "../belege/BelegeParallelbuchAuswertung";

const TITEL = {
  "pb-start": ["Parallelbuchhaltung", "Eigene Buchungen aus Bankumsätzen und Belegen – zum Vergleich mit der Buchhaltung des Steuerberaters."],
  "pb-einstellungen": ["Einstellungen und Zuordnungen", "Buchungsmethode, Konten und die Zuordnung der Bankkategorien zu SKR03-Konten. Nichts wird angenommen."],
  "pb-laeufe": ["Buchungsläufe", "Erzeugt vorläufige Buchungen für einen Zeitraum (wiederholbar, nichts wird gelöscht)."],
  "pb-journal": ["Buchungsjournal", "Alle vorläufigen Buchungen mit Sicherheitsstufe, Kontenzeilen und Prüfstatus."],
  "pb-susa": ["Summen- und Saldenliste", "Eigene SuSa je Monat, mit Anfangssalden aus der Kanzlei-SuSa."],
  "pb-bwa": ["Eigene BWA", "Betriebswirtschaftliche Auswertung der Parallelbuchhaltung (Struktur wie die Kanzlei-BWA)."],
};

export default function PbSeite({ seite, onNavigiere }) {
  const [st, setSt] = useState(null);
  const [fehler, setFehler] = useState("");
  const [hinweis, setHinweis] = useState("");
  const laden = useCallback(() => { pbApi.status().then(setSt).catch(e => setFehler(e.message)); }, []);
  useEffect(() => { laden(); }, [laden]);
  const [titel, untertitel] = TITEL[seite] || TITEL["pb-start"];
  if (!st) return <div>{fehler ? <Meldung fehler={fehler} /> : "Lädt…"}</div>;
  if (!st.aktiv) return <div><PbBanner /><Abschnitt titel="Parallelbuchhaltung"><span style={{ color: THEME.colors.textMuted }}>Nicht aktiviert.</span></Abschnitt></div>;
  return (
    <div style={{ color: THEME.colors.text }}>
      <PbBanner text={st.banner} />
      <Seitenkopf titel={titel} untertitel={untertitel} />
      <Meldung fehler={fehler} hinweis={hinweis} />
      {seite === "pb-start" && <Start st={st} onNavigiere={onNavigiere} setFehler={setFehler} />}
      {seite === "pb-einstellungen" && (
        <>
          <Einstellungen st={st} neuLaden={laden} setFehler={setFehler} setHinweis={setHinweis} />
          <Abschnitt titel="Zuordnung Bankkategorie → Konto"><Zuordnung setFehler={setFehler} setHinweis={setHinweis} /></Abschnitt>
        </>
      )}
      {seite === "pb-laeufe" && <Lauf st={st} neuLaden={laden} setFehler={setFehler} setHinweis={setHinweis} />}
      {seite === "pb-journal" && <Buchungen setFehler={setFehler} setHinweis={setHinweis} neuLaden={laden} />}
      {seite === "pb-susa" && <Auswertung setFehler={setFehler} setHinweis={setHinweis} teil="susa" />}
      {seite === "pb-bwa" && <Auswertung setFehler={setFehler} setHinweis={setHinweis} teil="bwa" />}
    </div>
  );
}

function Start({ st, onNavigiere, setFehler }) {
  const b = st.buchungen || {};
  return (
    <>
      <div style={kachelRaster}>
        <Kachel titel="Buchungsmethode" wert={st.konfiguriert ? st.methode : "nicht festgelegt"} farbe={st.konfiguriert ? THEME.colors.text : THEME.colors.danger}
          hinweis={st.konfiguriert ? "EÜR/Bilanz / Ist/Soll / Datumsregel" : "Es wird nichts gebucht, solange das nicht ausdrücklich festgelegt ist."} onClick={() => onNavigiere("buchhaltung", "pb-einstellungen")} />
        <Kachel titel="Aktuelle Buchungen" wert={b.aktiv ?? 0} hinweis={`${b.gesamt ?? 0} insgesamt (inkl. ersetzte/stornierte)`} onClick={() => onNavigiere("buchhaltung", "pb-journal")} />
        <Kachel titel="Unsichere Buchungen" wert={b.unsicher ?? 0} farbe={b.unsicher ? THEME.colors.danger : THEME.colors.success} hinweis="gelten nie automatisch als korrekt" onClick={() => onNavigiere("steuerberater", "unsichere")} />
        <Kachel titel="Ungeprüfte Buchungen" wert={b.ungeprueft ?? 0} farbe={b.ungeprueft ? THEME.colors.warning : THEME.colors.success} hinweis="noch nicht von Hand geprüft" onClick={() => onNavigiere("buchhaltung", "pb-journal")} />
      </div>
      {(st.konten_hinweise || []).length > 0 && (
        <Abschnitt titel="Hinweise zu den Konten">
          {st.konten_hinweise.map((h, i) => <div key={i} style={{ color: THEME.colors.warning }}>⚠️ {h}</div>)}
        </Abschnitt>
      )}
      <Abschnitt titel="Übersicht für einen Zeitraum"><Uebersicht setFehler={setFehler} /></Abschnitt>
      <div style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>Modus: {st.modus} · Lohn, Jahresabschluss und steuerliche Sonderbuchungen bleiben bei der Kanzlei.</div>
    </>
  );
}
